import React from "react"

import { styled, forwardRef, createStyles } from "@design-system/core"
import { CheckSolidIcon } from "@design-system/icons"

import { useCheckbox } from "./useCheckbox"
import { CheckboxProps } from "./types"

export const Checkbox = forwardRef<CheckboxProps, "label">((props, ref) => {
  const { children } = props

  const {
    state: { isFocused, isChecked, isDisabled, isInvalid },
    getInputProps,
    htmlProps,
  } = useCheckbox(props)

  const inputProps = getInputProps({}, ref)

  return (
    <styled.label
      style={[style.container, isDisabled && style.containerDisabled]}
      {...htmlProps}
    >
      <styled.input {...inputProps} style={style.input} />

      <styled.span
        style={[
          outerControlStyle.base,
          isChecked && outerControlStyle.active,
          isDisabled && outerControlStyle.disabled,
          isFocused && outerControlStyle.focus,
          isInvalid && outerControlStyle.invalid,
          isChecked && isDisabled && outerControlStyle.activeDisabled,
        ]}
      >
        <CheckSolidIcon
          style={[
            innerControlStyle.base,
            isDisabled && innerControlStyle.disabled,
            isChecked && isDisabled && innerControlStyle.activeDisabled,
          ]}
        />
      </styled.span>

      {children && (
        <styled.span
          style={[
            labelStyle.base,
            isChecked && labelStyle.isChecked,
            isDisabled && labelStyle.disabled,
          ]}
        >
          {children}
        </styled.span>
      )}
    </styled.label>
  )
})

const style = createStyles((theme) => ({
  container: {
    display: "inline-flex",
    alignItems: "center",
    cursor: "pointer",
  },
  containerDisabled: { cursor: "default" },
  input: {
    border: 0,
    clip: "rect(0px, 0px, 0px, 0px)",
    height: theme.sizes[1],
    width: theme.sizes[1],
    margin: -1,
    overflow: "hidden",
    padding: theme.sizes[0],
    whiteSpace: "nowrap" as "nowrap",
    position: "absolute" as "absolute",
  },
}))

const outerControlStyle = createStyles((theme) => ({
  base: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
    width: theme.sizes[20],
    height: theme.sizes[20],
    transition: "box-shadow 250ms",
    borderWidth: theme.borderWidths.small,
    borderStyle: "solid",
    borderColor: theme.colors.gray400,
    borderRadius: theme.borderRadii.small,
  },
  active: {
    backgroundColor: theme.colors.blue400,
    borderColor: theme.borderColors.selection,
  },
  disabled: {
    backgroundColor: theme.colors.gray050,
    borderColor: theme.borderColors.disabled,
  },
  activeDisabled: {
    backgroundColor: theme.colors.gray200,
    borderColor: theme.borderColors.disabled,
  },
  focus: {
    shadowColor: theme.colors.blue600,
    shadowRadius: theme.borderRadii.small,
  },
  invalid: { borderColor: theme.borderColors.error },
}))

const innerControlStyle = createStyles((theme) => ({
  base: { fill: theme.colors.white },
  disabled: { fill: theme.colors.gray050 },
  activeDisabled: { fill: theme.colors.white },
}))

const labelStyle = createStyles((theme) => ({
  base: {
    userSelect: "none",
    fontWeight: theme.fontWeights.regular,
    marginLeft: theme.spaces.spacing8,
    fontSize: theme.textFontSizes.medium,
  },
  isChecked: {
    fontWeight: theme.fontWeights.semibold,
  },
  disabled: {
    fontWeight: theme.fontWeights.regular,
    color: theme.textColors.disabled,
  },
}))
